import {DatePicker} from '@pleo-io/telescope'
import {Root, Content, Trigger, Portal} from '@radix-ui/react-popover'
import React, {useState} from 'react'
import styled, {css} from 'styled-components'

import {Text} from '@/components/text'
import {VisuallyHidden} from '@/components/visually-hidden'
import calendarIcon from '@/images/icons/calendar.svg'
import {inputStyles, Label} from '@/styles/inputs'
import {focusStyles} from '@/styles/utils'
import {tokens, colors, radii} from '@/theme'

interface Props {
    onChange?: (date: Date) => void
    value?: Date
    placeholder?: string
    label?: string
    error?: string | null
    id?: string
    required?: boolean
}

export const DateInput = ({
    label,
    placeholder = '\u00a0',
    value,
    onChange,
    error,
    id,
    required,
}: Props) => {
    const ref = React.useRef<HTMLInputElement>(null)
    const [isOpen, setIsOpen] = useState(false)
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(value)

    return (
        <Wrapper>
            <Root open={isOpen} onOpenChange={setIsOpen}>
                <VisuallyHidden
                    as="input"
                    readOnly
                    tabIndex={-1}
                    ref={ref}
                    type="date"
                    id={id}
                    required={required}
                    value={formatDate(selectedDate, 'yyyy-mm-dd')}
                />

                <StyledTrigger>
                    <StyledLabel htmlFor={id}>
                        <Label as="span" $required={required}>
                            {label}
                        </Label>

                        <DummyInput $isFocused={isOpen} $hasError={!!error}>
                            {selectedDate ? formatDate(selectedDate) : placeholder}
                        </DummyInput>
                    </StyledLabel>
                </StyledTrigger>
                <Portal>
                    <StyledContent
                        onOpenAutoFocus={(event) => {
                            event.preventDefault()
                        }}
                    >
                        <DatePicker
                            value={selectedDate}
                            //@ts-ignore
                            showNeighboringMonth
                            onClickDay={(date: Date) => {
                                setSelectedDate(date)
                                setIsOpen(false)
                                onChange && onChange(date)
                            }}
                        />
                    </StyledContent>
                </Portal>
            </Root>

            {error && (
                <Text $variant="label-base" $color="error">
                    {error}
                </Text>
            )}
        </Wrapper>
    )
}

const StyledLabel = styled.label`
    cursor: pointer;
    width: 100%;
    color: ${colors.text.muted};

    span:first-of-type {
        display: block;
        margin-bottom: ${tokens.spacing8};
    }
`

interface DummyInputProps {
    $isFocused?: boolean
    $hasError?: boolean
}

const DummyInput = styled.div<DummyInputProps>(
    ({$isFocused, $hasError}) => css`
        ${inputStyles({$hasError})}

        ${$isFocused && focusStyles()}

        background-color: ${colors.background.default};
        background-image: url(${calendarIcon});
        background-repeat: no-repeat;
        background-size: 24px;
        background-position: calc(100% - 20px) center;
        color: ${colors.text.lightMuted};
    `,
)

const StyledContent = styled(Content)`
    z-index: 1;
    width: 320px;
    padding: ${tokens.spacing20};
    margin: ${tokens.spacing10};
    border-radius: ${radii.arc8};
    border: ${({theme}) => theme.borders.default};
    background: ${colors.background.default};
    box-shadow: ${tokens.shadowElevateQuiet};
`

const StyledTrigger = styled(Trigger)`
    background-color: transparent;
    border: 0;
    padding: 0;
    text-align: left;
    display: flex;
    align-items: flex-start;

    &:focus ${DummyInput} {
        ${focusStyles()}
    }
`

const Wrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing8};
    width: 100%;

    [type='date'] {
        position: absolute;
        width: 1px;
        height: 1px;
        outline: none;
        border: none;
    }
`

const formatDate = (date?: Date, format: 'dd/mm/yyyy' | 'yyyy-mm-dd' = 'dd/mm/yyyy'): string => {
    if (!date) {
        return ''
    }

    const day = ('0' + date.getDate()).slice(-2)
    const month = ('0' + (date.getMonth() + 1)).slice(-2)
    const year = date.getFullYear()

    if (format === 'dd/mm/yyyy') {
        return `${day}/${month}/${year}`
    }

    if (format === 'yyyy-mm-dd') {
        return `${year}-${month}-${day}`
    }

    throw new Error('Invalid format')
}
